import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../Icon";
import { SHOP_CATEGORIES, SHOP_HIDDEN_TABS } from "../data";
import { useCart } from "../cart";
import { PageHead, useSeo } from "../ui";

export function formatPrice(price, note) {
  if (price === null || price === undefined) return "가격 문의";
  return `${price.toLocaleString("ko-KR")}원${note ? ` · ${note}` : ""}`;
}

/* 장바구니에 담을 수 있는 상품인가 — 가격이 있고 품절이 아니어야 한다 */
export function isBuyable(item) {
  return item.status !== "SOLDOUT" && item.price !== null && item.price !== undefined;
}

const TABS = ["전체", ...SHOP_CATEGORIES.filter((c) => !SHOP_HIDDEN_TABS.includes(c))];

function ShopCard({ item, onAdded }) {
  const [imgFailed, setImgFailed] = useState(false);
  const { add, requireLogin } = useCart();
  const soldout = item.status === "SOLDOUT";
  const buyable = isBuyable(item);

  const addToCart = (e) => {
    e.preventDefault();
    requireLogin(() => {
      add(item, 1);
      onAdded(item);
    });
  };

  return (
    <Link to={`/products/${item.slug}`} className="shop-card">
      <div className="shop-card__thumb">
        {item.thumb && !imgFailed ? (
          <img src={item.thumb} alt={item.name} loading="lazy" onError={() => setImgFailed(true)} />
        ) : (
          <div className="shop-card__empty">
            <Icon.Box width="26" height="26" aria-hidden="true" focusable="false" />
          </div>
        )}
        {soldout && <span className="shop-card__badge">품절</span>}
        {buyable && (
          <button
            type="button"
            className="shop-card__cart"
            onClick={addToCart}
            aria-label={`${item.name} 장바구니 담기`}
            title="장바구니 담기"
          >
            <Icon.Cart width="16" height="16" aria-hidden="true" focusable="false" />
          </button>
        )}
      </div>
      <div className="shop-card__body">
        {item.category && <div className="shop-card__cat">{item.category}</div>}
        <h3 className="shop-card__name">{item.name}</h3>
        <div className={`shop-card__price ${item.price == null ? "is-ask" : ""}`}>
          {item.price == null ? "가격 문의" : `${item.price.toLocaleString("ko-KR")}원`}
        </div>
      </div>
    </Link>
  );
}

export default function Products() {
  useSeo({
    title: "판매제품 — 퍼니키트·PCB모듈·소프트웨어 | I.META 아이닷메타",
    description:
      "직접 조립해 만들어 보는 퍼니키트, I.META가 직접 설계한 PCB모듈과 소프트웨어. 예제 레포와 핀맵, AI 코딩용 프로젝트 템플릿을 함께 제공합니다.",
    path: "/products",
  });

  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("cat")) ? params.get("cat") : "전체";
  const q = params.get("q") || "";
  const [draft, setDraft] = useState(q);
  const [state, setState] = useState({ status: "loading", items: [] });
  const [toast, setToast] = useState(null);
  const { count } = useCart();

  useEffect(() => setDraft(q), [q]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/products");
        if (!res.ok) throw new Error("목록을 불러오지 못했습니다.");
        const data = await res.json();
        if (alive) setState({ status: "ready", items: data.items || [] });
      } catch {
        if (alive) setState({ status: "error", items: [] });
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const update = (next) => {
    const merged = { cat: tab, q, ...next };
    const out = {};
    if (merged.cat && merged.cat !== "전체") out.cat = merged.cat;
    if (merged.q) out.q = merged.q;
    setParams(out, { replace: true });
  };

  const matched = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return state.items;
    return state.items.filter((i) =>
      [i.name, i.tagline, i.summary, i.category]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle))
    );
  }, [state.items, q]);

  const counts = useMemo(() => {
    const c = { 전체: matched.length };
    SHOP_CATEGORIES.forEach((k) => { c[k] = matched.filter((i) => i.category === k).length; });
    return c;
  }, [matched]);

  const shown = tab === "전체" ? matched : matched.filter((i) => i.category === tab);

  const onSearch = (e) => {
    e.preventDefault();
    update({ q: draft.trim() });
  };

  return (
    <section className="section shop">
      <PageHead
        kicker="PRODUCTS"
        title="판매제품"
        actions={
          <Link to="/cart" className="shop__cartlink">
            <Icon.Cart width="18" height="18" aria-hidden="true" focusable="false" />
            장바구니{count > 0 && <span className="shop__cartcount">{count}</span>}
          </Link>
        }
      />

      <form className="shop-search" role="search" onSubmit={onSearch}>
        <Icon.Search width="18" height="18" aria-hidden="true" focusable="false" className="shop-search__icon" />
        <input
          type="search"
          className="shop-search__input"
          placeholder="상품명, 설명으로 검색"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="상품 검색"
        />
        {q && (
          <button
            type="button"
            className="shop-search__clear"
            onClick={() => { setDraft(""); update({ q: "" }); }}
          >
            초기화
          </button>
        )}
        <button type="submit" className="shop-search__btn">검색</button>
      </form>

      <div className="shop-tabs" role="tablist" aria-label="상품 분류">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`shop-tabs__tab ${tab === t ? "is-active" : ""}`}
            onClick={() => update({ cat: t })}
          >
            {t}
            {state.status === "ready" && <span className="shop-tabs__count">{counts[t]}</span>}
          </button>
        ))}
      </div>

      {q && state.status === "ready" && (
        <p className="shop__result">
          ‘{q}’ 검색 결과 {shown.length}개
        </p>
      )}

      {state.status === "loading" && <p className="note-line">불러오는 중…</p>}

      {state.status === "error" && (
        <p className="note-line">목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</p>
      )}

      {state.status === "ready" && shown.length === 0 && (
        <div className="shop-empty">
          <Icon.Box width="30" height="30" aria-hidden="true" focusable="false" />
          <p>{q ? "검색 결과가 없습니다." : "이 분류에 등록된 상품이 아직 없습니다."}</p>
          <Link to="/contact" className="btn btn--ghost">
            찾는 상품 문의하기
          </Link>
        </div>
      )}

      {shown.length > 0 && (
        <div className="shop-grid">
          {shown.map((item) => (
            <ShopCard key={item.slug} item={item} onAdded={setToast} />
          ))}
        </div>
      )}

      {toast && (
        <div className="shop-toast" role="status">
          <span>‘{toast.name}’을(를) 장바구니에 담았습니다.</span>
          <Link to="/cart">장바구니 보기</Link>
        </div>
      )}
    </section>
  );
}
